import React, { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { getObservations, getHiveBatches } from '../../../../../../services/apiaryService';
import close from '../../../assets/images/close.png';
import * as Styles from '../../../Farmers/components/FarmerForm/style';
import { Row, Col, Button, Form } from 'react-bootstrap';
import LoadingOverlay from 'react-loading-overlay';
import BounceLoader from 'react-spinners/BounceLoader';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import { Select, DatePicker, Empty } from 'antd';
import moment from 'moment';
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css';
import 'react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css';
import GenericExportCSVModal from '../../../shared/GenericExportCSVModal';

const { Option } = Select;

// --- Helper Functions ---

const resolveImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    
    const baseUrl = (process.env.REACT_APP_API_APP_FIELD_URL || '').replace(/\/$/, '');
    const cleanUrl = url.startsWith('/') ? url : '/' + url;
    
    return baseUrl + cleanUrl;
};

// --- Styled Components ---

const MainContainer = styled.div`
    padding: 20px 0;
`;

const HeaderSection = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
`;

const FilterCard = styled.div`
    background: #f8f9fa;
    padding: 20px;
    border-radius: 12px;
    margin-bottom: 30px;
    border: 1px solid #e9ecef;
`;

const TableWrapper = styled.div`
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.03);
    overflow: hidden;
    border: 1px solid #eaeaea;

    .modern-table thead th {
        background: #f8f9fa;
        color: #636e72;
        font-weight: 700;
        font-size: 13px;
        padding: 15px;
        text-transform: uppercase;
        border-bottom: 2px solid #ff8701 !important;
    }

    .modern-table tbody td {
        padding: 15px;
        vertical-align: middle;
        font-size: 14px;
    }
`;

const StatusBadge = styled.span`
    display: inline-block;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 700;
    background-color: ${props => props.active ? '#e6fffa' : '#fff5f5'};
    color: ${props => props.active ? '#00b894' : '#d63031'};
    border: 1px solid ${props => props.active ? '#b2f2bb' : '#ffa8a8'};
`;

const PhotoGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 15px;
    margin-top: 15px;
`;

const GeotaggedPhoto = styled.div`
    position: relative;
    width: 100%;
    aspect-ratio: 4/3;
    border-radius: 8px;
    overflow: hidden;
    background: #f0f0f0;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    cursor: pointer;
    transition: transform 0.2s;

    &:hover {
        transform: scale(1.02);
    }

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const PhotoOverlay = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(transparent, rgba(0,0,0,0.8));
    color: white;
    padding: 8px;
    font-size: 10px;
    line-height: 1.2;

    .geo-data {
        display: flex;
        align-items: center;
        gap: 5px;
        margin-bottom: 2px;
    }

    .time-data {
        opacity: 0.8;
    }
`;

// --- Badge/Helper components for Scientific Report ---

const renderStatusBadge = (val) => {
    const isYes = val === 'yes' || val === 'YES' || val === true || val === 'Yes' || val === '✓';
    return (
        <span style={{
            display: 'inline-block', padding: '4px 10px', borderRadius: '20px',
            fontSize: '12px', fontWeight: '600', textTransform: 'uppercase',
            backgroundColor: isYes ? '#E6F4EA' : '#F1F3F4',
            color: isYes ? '#137333' : '#5F6368',
            border: `1px solid ${isYes ? '#CEEAD6' : '#DADCE0'}`
        }}>
            {isYes ? '✓ Yes' : '✗ No'}
        </span>
    );
};

const renderSeverityBadge = (severity) => {
    if (!severity) return <span style={{ color: '#999' }}>—</span>;
    const sev = severity.toUpperCase();
    let bg = '#F1F3F4', fg = '#5F6368', border = '#DADCE0';
    if (sev === 'LOW') { bg = '#FEF7E0'; fg = '#B06000'; border = '#FEEFC3'; }
    else if (sev === 'MEDIUM') { bg = '#FEEFC3'; fg = '#C26401'; border = '#FFE082'; }
    else if (sev === 'HIGH') { bg = '#FAD2E1'; fg = '#A71C49'; border = '#F8B4D9'; }
    return (
        <span style={{
            display: 'inline-block', padding: '4px 10px', borderRadius: '20px',
            fontSize: '12px', fontWeight: '600', textTransform: 'uppercase',
            backgroundColor: bg, color: fg, border: `1px solid ${border}`
        }}>
            {severity}
        </span>
    );
};

const renderRatingBadge = (rating) => {
    if (!rating) return <span style={{ color: '#999' }}>—</span>;
    const r = rating.toUpperCase();
    let bg = '#F1F3F4', fg = '#5F6368', border = '#DADCE0';
    if (r === 'GOOD' || r === 'YES') { bg = '#E6F4EA'; fg = '#137333'; border = '#CEEAD6'; }
    else if (r === 'AVERAGE' || r === 'MODERATE') { bg = '#FEF7E0'; fg = '#B06000'; border = '#FEEFC3'; }
    else if (r === 'POOR' || r === 'NO') { bg = '#FCE8E6'; fg = '#C5221F'; border = '#FAD2E1'; }
    return (
        <span style={{
            display: 'inline-block', padding: '4px 10px', borderRadius: '20px',
            fontSize: '12px', fontWeight: '600', textTransform: 'uppercase',
            backgroundColor: bg, color: fg, border: `1px solid ${border}`
        }}>
            {rating}
        </span>
    );
};

const SciSectionTitle = ({ children }) => (
    <h4 style={{
        color: '#EF5A20', fontSize: '17px', fontWeight: '700',
        marginTop: '22px', marginBottom: '12px',
        borderBottom: '2px solid #FEE5D9', paddingBottom: '6px'
    }}>
        {children}
    </h4>
);

const formatDateTimeSci = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })
        + ' at ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const renderScientificReport = (data) => {
    const si = data.scientific_inspection || {};
    const gi = si.general_info || {};
    const cd = si.colony_details || {};
    const weakReasons = si.weak_colonies_reasons || {};
    const checklist = si.colony_checklist || {};
    const pests = si.pest_predator_observation || {};
    const diseases = si.disease_observation || {};
    const env = si.floral_environmental || {};
    const hm = si.hive_management || {};
    const suggestions = si.suggestions || {};
    const nextVisit = si.next_visit || {};
    const remarks = si.final_remarks_inspector || [];
    const photos = data.apiary_photos || data.photos || data.mediaUrls || [];

    return (
        <div style={{ fontFamily: 'Inter, sans-serif', color: '#262626', padding: '10px' }}>

            {/* 1. General Information */}
            <SciSectionTitle>General Information</SciSectionTitle>
            <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '14px', backgroundColor: '#FAFAFA', padding: '16px',
                borderRadius: '8px', border: '1px solid #EAEAEA', marginBottom: '20px'
            }}>
                <div><strong>Date of Visit:</strong> {formatDateTimeSci(data.created_at || data.observation_date)}</div>
                <div><strong>Time of Inspection:</strong> {gi.time_of_inspection || 'N/A'}</div>
                <div><strong>SHG / PG / Cluster Name:</strong> {gi.shg_pg_cluster_name || 'N/A'}</div>
                <div><strong>Village:</strong> {gi.village || 'N/A'}</div>
                <div><strong>Gram Panchayat:</strong> {gi.gram_panchayat || 'N/A'}</div>
                <div><strong>Block / Mandal:</strong> {gi.block_mandal || 'N/A'}</div>
                <div><strong>District:</strong> {gi.district || 'N/A'}</div>
                <div><strong>State:</strong> {gi.state || 'N/A'}</div>
                <div><strong>Bee-Mitra / Inspector Name:</strong> {gi.bee_mitra_inspector || 'N/A'}</div>
                <div><strong>Bee Species:</strong> {gi.bee_species || 'N/A'}</div>
                <div><strong>Photo Captured:</strong> {photos.length > 0 ? 'Yes' : 'No'}</div>
            </div>

            {/* 2. Colony Details */}
            <SciSectionTitle>Colony Details</SciSectionTitle>
            <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                gap: '12px', marginBottom: '20px'
            }}>
                {[
                    { label: 'Total Colonies Available', val: cd.total_colonies_available || data.total_hives },
                    { label: 'Colonies Inspected', val: cd.number_of_colonies_inspected },
                    { label: 'Active Colonies', val: cd.number_of_active_colonies || data.active_colonies },
                    { label: 'Weak Colonies', val: cd.number_of_weak_colonies },
                    { label: 'Inactive / Dead Colonies', val: cd.number_of_inactive_dead_colonies || data.empty_hives },
                    { label: 'Migrated / Absconded', val: cd.number_of_absconded_colonies || data.absconded_hives },
                    { label: 'Queenless Colonies', val: cd.number_of_queenless_colonies },
                ].map((item, idx) => (
                    <div key={idx} style={{
                        backgroundColor: '#FAFAFA', border: '1px solid #EAEAEA',
                        borderRadius: '8px', padding: '12px', textAlign: 'center'
                    }}>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#EF5A20' }}>{item.val ?? 0}</div>
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>{item.label}</div>
                    </div>
                ))}
            </div>

            {/* 3. Reason for Inactive / Weak Colonies */}
            <SciSectionTitle>Reason for Inactive / Weak Colonies</SciSectionTitle>
            <div style={{
                backgroundColor: '#FAFAFA', padding: '16px', borderRadius: '8px',
                border: '1px solid #EAEAEA', marginBottom: '20px'
            }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px' }}>
                    {[
                        { key: 'absconding', label: 'Absconding' },
                        { key: 'queen_loss', label: 'Queen Loss' },
                        { key: 'lack_of_nectar', label: 'Lack of Nectar' },
                        { key: 'lack_of_pollen', label: 'Lack of Pollen' },
                        { key: 'pest_attack', label: 'Pest Attack' },
                        { key: 'disease_symptoms', label: 'Disease Symptoms' },
                        { key: 'starvation', label: 'Starvation' },
                        { key: 'excess_heat', label: 'Excess Heat' },
                        { key: 'rain_moisture_problem', label: 'Rain / Moisture Problem' },
                        { key: 'pesticide_exposure', label: 'Pesticide Exposure' },
                        { key: 'poor_hive_maintenance', label: 'Poor Hive Maintenance' },
                        { key: 'old_damaged_comb', label: 'Old / Damaged Comb' },
                        { key: 'ant_attack', label: 'Ant Attack' },
                        { key: 'wax_moth_infestation', label: 'Wax Moth Infestation' },
                        { key: 'colony_merging_required', label: 'Colony Merging Required' },
                    ].map((item) => (
                        <div key={item.key} style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '8px 12px', backgroundColor: '#FFF',
                            border: '1px solid #EAEAEA', borderRadius: '6px'
                        }}>
                            <span>{item.label}</span>
                            {renderStatusBadge(weakReasons[item.key])}
                        </div>
                    ))}
                </div>
                {si.weak_colonies_reasons_other_remarks ? (
                    <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#FFF', borderRadius: '6px', border: '1px solid #EAEAEA' }}>
                        <strong>Other Weakness Remarks:</strong> {si.weak_colonies_reasons_other_remarks}
                    </div>
                ) : null}
            </div>

            {/* 4. Colony Inspection Checklist */}
            <SciSectionTitle>Colony Inspection Checklist (Colony Strength & Brood Status)</SciSectionTitle>
            <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '10px', marginBottom: '20px'
            }}>
                {[
                    { key: 'Queen Present', label: 'Queen Present' },
                    { key: 'Fresh Eggs Seen', label: 'Fresh Eggs Seen' },
                    { key: 'Larvae Present', label: 'Larvae Present' },
                    { key: 'Sealed Brood Present', label: 'Sealed Brood Present' },
                    { key: 'Brood Pattern Healthy', label: 'Brood Pattern Healthy' },
                    { key: 'Brood Pattern Unhealthy / Scattered', label: 'Brood Pattern Unhealthy / Scattered' },
                    { key: 'Drone Brood Excessive', label: 'Drone Brood Excessive' },
                    { key: 'Queen Cells Present', label: 'Queen Cells Present' },
                    { key: 'Worker Laying Symptoms', label: 'Worker Laying Symptoms' },
                    { key: 'Colony Population Strong', label: 'Colony Population Strong' },
                    { key: 'Honey Storage Adequate', label: 'Honey Storage Adequate' },
                    { key: 'Pollen Storage Adequate', label: 'Pollen Storage Adequate' },
                ].map((item) => (
                    <div key={item.key} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '10px 16px', backgroundColor: '#FAFAFA',
                        border: '1px solid #EAEAEA', borderRadius: '6px'
                    }}>
                        <span>{item.label}</span>
                        {renderStatusBadge(checklist[item.key])}
                    </div>
                ))}
            </div>

            {/* 5. Pest & Predator Observation */}
            <SciSectionTitle>Pest & Predator Observation</SciSectionTitle>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
                <thead>
                    <tr style={{ backgroundColor: '#F8F9FA' }}>
                        <th style={{ padding: '12px', borderBottom: '2px solid #EAEAEA', textAlign: 'left' }}>Pest / Predator</th>
                        <th style={{ padding: '12px', borderBottom: '2px solid #EAEAEA', textAlign: 'center' }}>Observed</th>
                        <th style={{ padding: '12px', borderBottom: '2px solid #EAEAEA', textAlign: 'center' }}>Severity</th>
                        <th style={{ padding: '12px', borderBottom: '2px solid #EAEAEA', textAlign: 'left' }}>Remarks</th>
                    </tr>
                </thead>
                <tbody>
                    {['Ants', 'Cockroaches', 'Wax Moth', 'Spider Webs', 'Lizards',
                        'Birds / Bee Eaters', 'Wasps / Hornets', 'Termites', 'Rats / Rodents',
                        'Small Hive Beetle', 'Frogs'
                    ].map((pest) => {
                        const obs = pests[pest] || { observed: 'no', severity: 'Low', remarks: '' };
                        return (
                            <tr key={pest} style={{ borderBottom: '1px solid #F0F0F0' }}>
                                <td style={{ padding: '10px 12px' }}><strong>{pest}</strong></td>
                                <td style={{ padding: '10px 12px', textAlign: 'center' }}>{renderStatusBadge(obs.observed)}</td>
                                <td style={{ padding: '10px 12px', textAlign: 'center' }}>{renderSeverityBadge(obs.severity)}</td>
                                <td style={{ padding: '10px 12px', color: '#666' }}>{obs.remarks || '—'}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {/* 6. Disease Observation */}
            <SciSectionTitle>Disease Observation</SciSectionTitle>
            <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
                gap: '10px', marginBottom: '20px'
            }}>
                {[
                    { key: 'Foul Smell from Brood', label: 'Foul Smell from Brood' },
                    { key: 'Chalk Brood Symptoms', label: 'Chalk Brood Symptoms' },
                    { key: 'Sac Brood Symptoms', label: 'Sac Brood Symptoms' },
                    { key: 'Deformed Bees', label: 'Deformed Bees' },
                    { key: 'Dead Brood Seen', label: 'Dead Brood Seen' },
                    { key: 'Mold / Fungal Growth', label: 'Mold / Fungal Growth' },
                    { key: 'Dysentery Symptoms', label: 'Dysentery Symptoms' },
                    { key: 'Other Disease Symptoms', label: 'Other Disease Symptoms' },
                ].map((item) => (
                    <div key={item.key} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '10px 16px', backgroundColor: '#FAFAFA',
                        border: '1px solid #EAEAEA', borderRadius: '6px'
                    }}>
                        <span>{item.label}</span>
                        {renderStatusBadge(diseases[item.key])}
                    </div>
                ))}
            </div>

            {/* 7. Floral & Environmental Condition */}
            <SciSectionTitle>Floral & Environmental Condition</SciSectionTitle>
            <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                gap: '14px', backgroundColor: '#FAFAFA', padding: '16px',
                borderRadius: '8px', border: '1px solid #EAEAEA', marginBottom: '20px'
            }}>
                <div><strong>Major Flowering Crops Nearby:</strong> {env.flowering_crops_nearby || 'N/A'}</div>
                <div><strong>Nectar Availability:</strong> {renderRatingBadge(env.nectar_availability)}</div>
                <div><strong>Pollen Availability:</strong> {renderRatingBadge(env.pollen_availability)}</div>
                <div><strong>Water Source Available Nearby:</strong> {renderRatingBadge(env.water_source_nearby)}</div>
                <div><strong>Shade Availability:</strong> {renderRatingBadge(env.shade_availability)}</div>
                <div><strong>Pesticide Spraying Nearby:</strong> {renderRatingBadge(env.pesticide_spraying_nearby)}</div>
                <div><strong>Weather Condition During Visit:</strong> {env.weather_condition || 'N/A'}</div>
                <div><strong>Migration Required:</strong> {renderRatingBadge(env.migration_required)}</div>
            </div>

            {/* 8. Hive Management Condition */}
            <SciSectionTitle>Hive Management Condition</SciSectionTitle>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
                <thead>
                    <tr style={{ backgroundColor: '#F8F9FA' }}>
                        <th style={{ padding: '12px', borderBottom: '2px solid #EAEAEA', textAlign: 'left' }}>Inspection Point</th>
                        <th style={{ padding: '12px', borderBottom: '2px solid #EAEAEA', textAlign: 'center' }}>Rating</th>
                        <th style={{ padding: '12px', borderBottom: '2px solid #EAEAEA', textAlign: 'left' }}>Remarks</th>
                    </tr>
                </thead>
                <tbody>
                    {[
                        { key: 'cleanliness', label: 'Hive Cleanliness' },
                        { key: 'bottom_board', label: 'Bottom Board Condition' },
                        { key: 'frame_condition', label: 'Frame Condition' },
                        { key: 'comb_condition', label: 'Comb Condition' },
                        { key: 'hive_stand', label: 'Hive Stand Condition' },
                        { key: 'protection_from_ants', label: 'Protection from Ants' },
                        { key: 'ventilation', label: 'Ventilation' },
                        { key: 'shade_arrangement', label: 'Shade Arrangement' },
                        { key: 'water_arrangement', label: 'Water Arrangement' }
                    ].map((item) => {
                        const val = hm[item.key] || { rating: 'good', remarks: '' };
                        return (
                            <tr key={item.key} style={{ borderBottom: '1px solid #F0F0F0' }}>
                                <td style={{ padding: '12px' }}><strong>{item.label}</strong></td>
                                <td style={{ padding: '12px', textAlign: 'center' }}>{renderRatingBadge(val.rating)}</td>
                                <td style={{ padding: '12px', color: '#666' }}>{val.remarks || '—'}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {/* 9. Suggestions / Advisory Given */}
            <SciSectionTitle>Suggestions / Advisory Given to Beekeeper</SciSectionTitle>
            <div style={{
                backgroundColor: '#FAFAFA', padding: '16px', borderRadius: '8px',
                border: '1px solid #EAEAEA', marginBottom: '20px'
            }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '10px', marginBottom: '12px' }}>
                    {[
                        { key: 'sugar_syrup_feeding', label: 'Sugar Syrup Feeding' },
                        { key: 'pollen_patty_feeding', label: 'Pollen Patty Feeding' },
                        { key: 'regular_hive_inspection', label: 'Regular Hive Inspection' },
                        { key: 'deep_cleaning_hive', label: 'Deep Cleaning of Hive' },
                        { key: 'wax_moth_control', label: 'Wax Moth Control' },
                        { key: 'ant_protection', label: 'Ant Protection Measures' },
                        { key: 'comb_replacement', label: 'Comb Replacement' },
                        { key: 'colony_strengthening', label: 'Colony Strengthening' },
                        { key: 'colony_merging', label: 'Colony Merging' },
                        { key: 'queen_replacement', label: 'Queen Replacement' },
                        { key: 'hive_shifting_migration', label: 'Hive Shifting / Migration' },
                        { key: 'provide_shade', label: 'Provide Shade' },
                        { key: 'provide_water_source', label: 'Provide Water Source' },
                        { key: 'pest_monitoring', label: 'Pest Monitoring' },
                        { key: 'honey_super_addition', label: 'Honey Super Addition' },
                        { key: 'honey_harvesting_guidance', label: 'Honey Harvesting Guidance' },
                        { key: 'swarm_prevention', label: 'Swarm Prevention Measures' },
                        { key: 'record_keeping', label: 'Record Keeping Guidance' },
                    ].map((item) => (
                        <div key={item.key} style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '8px 12px', backgroundColor: '#FFF',
                            border: '1px solid #EAEAEA', borderRadius: '6px'
                        }}>
                            <span>{item.label}</span>
                            {renderStatusBadge(suggestions[item.key])}
                        </div>
                    ))}
                </div>
                {si.suggestions_details_remarks ? (
                    <div style={{ padding: '12px', backgroundColor: '#FFF', borderRadius: '6px', border: '1px solid #EAEAEA' }}>
                        <strong>Details of Suggestions:</strong>
                        <p style={{ margin: '8px 0 0 0', whiteSpace: 'pre-line', color: '#444' }}>
                            {si.suggestions_details_remarks}
                        </p>
                    </div>
                ) : null}
            </div>

            {/* 10. Next Visit Plan */}
            <SciSectionTitle>Next Visit Plan</SciSectionTitle>
            <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: '14px', backgroundColor: '#FAFAFA', padding: '16px',
                borderRadius: '8px', border: '1px solid #EAEAEA', marginBottom: '20px'
            }}>
                <div><strong>Proposed Date of Next Visit:</strong> {nextVisit.proposed_date ? new Date(nextVisit.proposed_date).toLocaleDateString('en-GB') : 'N/A'}</div>
                <div><strong>Purpose of Next Visit:</strong> {nextVisit.purpose || 'N/A'}</div>
                <div><strong>Follow-up Required:</strong> {renderStatusBadge(nextVisit.follow_up_required)}</div>
            </div>

            {/* 11. Final Remarks of Inspector */}
            {remarks && remarks.length > 0 ? (
                <>
                    <SciSectionTitle>Final Remarks of Inspector</SciSectionTitle>
                    <div style={{
                        backgroundColor: '#FAFAFA', padding: '16px', borderRadius: '8px',
                        border: '1px solid #EAEAEA', marginBottom: '20px'
                    }}>
                        <ul style={{ margin: 0, paddingLeft: '20px' }}>
                            {remarks.map((r, index) => (
                                <li key={index} style={{ marginBottom: '8px', fontSize: '15px' }}>{r}</li>
                            ))}
                        </ul>
                    </div>
                </>
            ) : null}

            {/* 12. Photos */}
            {photos.length > 0 ? (
                <>
                    <SciSectionTitle>Photos Captured</SciSectionTitle>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginTop: '10px' }}>
                        {photos.map((item, idx) => {
                            const url = typeof item === 'string' ? item : (item.url ? resolveImageUrl(item.url) : '');
                            return (
                                <img
                                    key={url || idx}
                                    src={url}
                                    alt={`inspection-${idx}`}
                                    onClick={() => url && window.open(url, '_blank')}
                                    style={{
                                        width: '240px', height: '180px', objectFit: 'cover',
                                        borderRadius: '6px', border: '1px solid #DDD', cursor: 'pointer'
                                    }}
                                    onError={(e) => { e.target.src = 'https://placehold.co/240x180?text=Photo+Error'; }}
                                />
                            );
                        })}
                    </div>
                </>
            ) : null}

        </div>
    );
};

// --- Observation Detail Modal ---

const ObservationDetailModal = ({ onClose, data }) => {
    if (!data) return null;

    const isScientific = !!data.scientific_inspection;
    const hasPhotos = data.apiary_photos && data.apiary_photos.length > 0;

    return (
        <Styles.ModalWrapper style={{ zIndex: 1060 }}>
            <Styles.ModalContent2 style={{
                maxWidth: isScientific ? '900px' : '800px',
                width: '92%',
                maxHeight: '92vh',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
            }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px 0 24px', flexShrink: 0 }}>
                    <div>
                        <h3 style={{ color: '#EF5A20', margin: 0, fontSize: '20px', fontWeight: '700' }}>
                            {isScientific ? 'Scientific Beekeeping Colony Inspection Report' : 'Observation Details'}
                        </h3>
                        {data.latitude && data.longitude ? (
                            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#666' }}>
                                Location:{' '}
                                <a
                                    href={`https://www.google.com/maps/search/?api=1&query=${data.latitude},${data.longitude}`}
                                    target="_blank" rel="noreferrer"
                                    style={{ color: '#EF5A20', textDecoration: 'underline', fontWeight: '600' }}
                                >
                                    {data.latitude}, {data.longitude}
                                </a>
                            </p>
                        ) : null}
                    </div>
                    <img src={close} alt="Close" onClick={onClose} style={{ width: 20, cursor: 'pointer', opacity: 0.6, flexShrink: 0 }} />
                </div>

                {/* Scrollable Body */}
                <div style={{ padding: '16px 24px 24px', overflowY: 'auto', flex: 1 }}>
                    {isScientific ? (
                        renderScientificReport(data)
                    ) : (
                        <>
                            <div style={{ background: '#f9f9f9', padding: 15, borderRadius: 8, marginBottom: 20 }}>
                                <Row>
                                    <Col md={6}>
                                        <p style={{ margin: '0 0 5px' }}><strong>Date:</strong> {moment(data.observation_date).format('DD MMM YYYY')}</p>
                                        <p style={{ margin: 0 }}><strong>Batch ID:</strong> {data.batch_id}</p>
                                    </Col>
                                    <Col md={6} className="text-right">
                                        <StatusBadge active={data.active_colonies > 0}>
                                            {data.active_colonies > 0 ? 'Active Colonies Present' : 'No Active Colonies'}
                                        </StatusBadge>
                                    </Col>
                                </Row>
                            </div>

                            <h4 style={{ borderBottom: '1px solid #eee', paddingBottom: 8, marginBottom: 15 }}>Hive Metrics</h4>
                            <Row>
                                <Col md={4}>
                                    <div style={{ textAlign: 'center', padding: 10, background: '#e6fffa', borderRadius: 8 }}>
                                        <h2 style={{ margin: 0, color: '#00b894' }}>{data.active_colonies}</h2>
                                        <p style={{ margin: 0, fontSize: 12, fontWeight: 600 }}>Active Colonies</p>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div style={{ textAlign: 'center', padding: 10, background: '#fff5f5', borderRadius: 8 }}>
                                        <h2 style={{ margin: 0, color: '#d63031' }}>{data.empty_hives}</h2>
                                        <p style={{ margin: 0, fontSize: 12, fontWeight: 600 }}>Empty Hives</p>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div style={{ textAlign: 'center', padding: 10, background: '#f0f0f0', borderRadius: 8 }}>
                                        <h2 style={{ margin: 0, color: '#636e72' }}>{data.absconded_hives}</h2>
                                        <p style={{ margin: 0, fontSize: 12, fontWeight: 600 }}>Absconded Hives</p>
                                    </div>
                                </Col>
                            </Row>
                            <div style={{ marginTop: 20, padding: '10px 15px', background: '#f8f9fa', borderRadius: 8, display: 'flex', justifyContent: 'space-between' }}>
                                <strong>Total Hives Recorded:</strong>
                                <span style={{ fontWeight: 700, color: '#ff8701' }}>{data.total_hives}</span>
                            </div>

                            <div style={{ marginTop: 25 }}>
                                <h4 style={{ borderBottom: '1px solid #eee', paddingBottom: 8, marginBottom: 15 }}>Apiary Photos (Geotagged)</h4>
                                {hasPhotos ? (
                                    <PhotoGrid>
                                        {data.apiary_photos.map((photo, index) => {
                                            const fullUrl = resolveImageUrl(photo.url);
                                            return (
                                                <GeotaggedPhoto key={index} onClick={() => window.open(fullUrl, '_blank')}>
                                                    <img src={fullUrl} alt={`Observation ${index + 1}`} onError={(e) => { e.target.src = 'https://placehold.co/400x300?text=Photo+Error'; }} />
                                                    <PhotoOverlay>
                                                        <div className="geo-data">
                                                            <i className="fa fa-map-marker"></i>
                                                            {photo.lat?.toFixed(6)}, {photo.lng?.toFixed(6)}
                                                        </div>
                                                        <div className="time-data">
                                                            <i className="fa fa-clock-o mr-1"></i>
                                                            {photo.captured_at ? moment(photo.captured_at).format('DD/MM/YY HH:mm') : 'N/A'}
                                                        </div>
                                                    </PhotoOverlay>
                                                </GeotaggedPhoto>
                                            );
                                        })}
                                    </PhotoGrid>
                                ) : (
                                    <div style={{ padding: '30px', textAlign: 'center', background: '#f8f9fa', borderRadius: 8, color: '#999' }}>
                                        <i className="fa fa-picture-o fa-2x mb-2" style={{ opacity: 0.3 }}></i>
                                        <p style={{ margin: 0 }}>No photos available for this observation.</p>
                                    </div>
                                )}
                            </div>

                            <div style={{ marginTop: 25 }}>
                                <h4 style={{ borderBottom: '1px solid #eee', paddingBottom: 8, marginBottom: 15 }}>Remarks</h4>
                                <p style={{
                                    padding: 15, background: '#fff9f5', borderRadius: 8,
                                    border: '1px solid #ffe8d6',
                                    fontStyle: data.remarks ? 'normal' : 'italic',
                                    color: data.remarks ? '#2d3436' : '#999', margin: 0
                                }}>
                                    {data.remarks || 'No remarks provided.'}
                                </p>
                            </div>
                        </>
                    )}
                </div>
            </Styles.ModalContent2>
        </Styles.ModalWrapper>
    );
};



const NectarObservations = ({ apiaryId }) => {
    const [observations, setObservations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedObservation, setSelectedObservation] = useState(null);
    const [showExportModal, setShowExportModal] = useState(false);

    // Filters
    const [activeOnlyFilter, setActiveOnlyFilter] = useState(false);
    const [dateRange, setDateRange] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    const fetchObservations = async () => {
        if (!apiaryId) return;
        setLoading(true);
        try {
            const data = await getObservations(apiaryId);
            setObservations(data || []);
        } catch (err) {
            console.error("Failed to fetch observations:", err);
            setObservations([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchObservations();
    }, [apiaryId]);

    const filteredObservations = useMemo(() => {
        return observations.filter(obs => {
            // Status Filter
            const matchesStatus = !activeOnlyFilter || obs.active_colonies > 0;
            
            // Date Filter
            let matchesDate = true;
            if (dateRange && dateRange[0] && dateRange[1]) {
                const obsDate = moment(obs.observation_date);
                matchesDate = obsDate.isBetween(dateRange[0], dateRange[1], 'day', '[]');
            }

            // Search Filter (Remarks)
            const searchText = searchTerm.toLowerCase();
            const matchesSearch = !searchTerm || obs.remarks?.toLowerCase().includes(searchText);

            return matchesStatus && matchesDate && matchesSearch;
        }).sort((a, b) => new Date(b.observation_date) - new Date(a.observation_date));
    }, [observations, activeOnlyFilter, dateRange, searchTerm]);

    const columns = [
        {
            dataField: 'observation_date',
            text: 'Date',
            sort: true,
            formatter: (cell) => moment(cell).format('DD MMM YYYY'),
            style: { fontWeight: 'bold' }
        },
        {
            dataField: 'active_colonies',
            text: 'Active',
            sort: true,
            formatter: (cell) => <span style={{ color: '#00b894', fontWeight: 700 }}>{cell}</span>,
            headerStyle: { textAlign: 'center' },
            style: { textAlign: 'center' }
        },
        {
            dataField: 'empty_hives',
            text: 'Empty',
            sort: true,
            formatter: (cell) => <span style={{ color: '#d63031', fontWeight: 700 }}>{cell}</span>,
            headerStyle: { textAlign: 'center' },
            style: { textAlign: 'center' }
        },
        {
            dataField: 'absconded_hives',
            text: 'Absconded',
            sort: true,
            formatter: (cell) => <span style={{ color: '#636e72', fontWeight: 700 }}>{cell}</span>,
            headerStyle: { textAlign: 'center' },
            style: { textAlign: 'center' }
        },
        {
            dataField: 'status',
            text: 'Status',
            formatter: (_, row) => (
                <StatusBadge active={row.active_colonies > 0}>
                    {row.active_colonies > 0 ? 'Active' : 'No Activity'}
                </StatusBadge>
            )
        },
        {
            dataField: 'remarks',
            text: 'Remarks',
            formatter: (cell) => (
                <div style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#666', fontSize: '13px' }}>
                    {cell || '-'}
                </div>
            )
        },
        {
            dataField: 'action',
            text: '',
            formatter: (_, row) => (
                <Button 
                    variant="link" 
                    size="sm" 
                    onClick={() => setSelectedObservation(row)}
                    style={{ color: '#ff8701', textDecoration: 'none', fontWeight: 700 }}
                >
                    View Details
                </Button>
            ),
            headerStyle: { width: '120px' }
        }
    ];

    const EXPORT_COLUMNS = [
        { label: 'Date', key: 'observation_date', formatter: (row) => row.observation_date ? moment(row.observation_date).format('DD MMM YYYY') : '' },
        { label: 'Active Colonies', key: 'active_colonies', path: 'active_colonies' },
        { label: 'Empty Hives', key: 'empty_hives', path: 'empty_hives' },
        { label: 'Absconded Hives', key: 'absconded_hives', path: 'absconded_hives' },
        { label: 'Total Hives', key: 'total_hives', path: 'total_hives' },
        { label: 'Status', key: 'status', formatter: (row) => row.active_colonies > 0 ? 'Active' : 'No Activity' },
        { label: 'Remarks', key: 'remarks', formatter: (row) => row.remarks || '' }
    ];

    return (
        <MainContainer>
            <HeaderSection>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <h4 style={{ margin: 0, color: '#2d3436' }}>Hive Observations</h4>
                    <div style={{ background: '#ff8701', color: 'white', padding: '2px 10px', borderRadius: '15px', fontSize: '12px', fontWeight: 700 }}>
                        {observations.length} Total
                    </div>
                </div>
                <Button
                    variant="outline-secondary"
                    onClick={() => setShowExportModal(true)}
                    style={{ fontWeight: 700, borderRadius: '8px' }}
                >
                    <i className="fa fa-download mr-2"></i> Export CSV
                </Button>
            </HeaderSection>

            <FilterCard>
                <Row className="align-items-end">
                    <Col md={3}>
                        <Form.Label style={{ fontSize: '13px', fontWeight: 600 }}>Quick Filter</Form.Label>
                        <Select 
                            style={{ width: '100%' }} 
                            value={activeOnlyFilter} 
                            onChange={v => setActiveOnlyFilter(v)}
                        >
                            <Option value={false}>All Observations</Option>
                            <Option value={true}>Active Colonies Only</Option>
                        </Select>
                    </Col>
                    <Col md={4}>
                        <Form.Label style={{ fontSize: '13px', fontWeight: 600 }}>Date Range</Form.Label>
                        <DatePicker.RangePicker 
                            style={{ width: '100%' }} 
                            value={dateRange}
                            onChange={dates => setDateRange(dates)} 
                        />
                    </Col>
                    <Col md={3}>
                        <Form.Label style={{ fontSize: '13px', fontWeight: 600 }}>Search Remarks</Form.Label>
                        <Form.Control 
                            placeholder="Type remarks..." 
                            value={searchTerm} 
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </Col>
                    <Col md={2} className="text-right">
                        <Button 
                            variant="outline-secondary" 
                            style={{ width: '100%', fontWeight: 600, borderRadius: '8px' }}
                            onClick={() => {
                                setActiveOnlyFilter(false);
                                setDateRange(null);
                                setSearchTerm('');
                            }}
                        >
                            Reset
                        </Button>
                    </Col>
                </Row>
            </FilterCard>

            <LoadingOverlay
                active={loading}
                spinner={<BounceLoader color={'#ff8701'} />}
                text='Loading inspections...'
                styles={{
                    overlay: (base) => ({
                        ...base,
                        background: 'rgba(255, 255, 255, 0.7)',
                        zIndex: 10
                    })
                }}
            >
                {filteredObservations.length === 0 && !loading ? (
                    <Empty 
                        image={Empty.PRESENTED_IMAGE_SIMPLE} 
                        description="No observations found" 
                        style={{ padding: '60px 0', background: '#fff', borderRadius: '12px', border: '1px dashed #ddd' }}
                    />
                ) : (
                    <TableWrapper>
                        <BootstrapTable
                            keyField="_id"
                            data={filteredObservations}
                            columns={columns}
                            hover
                            bordered={false}
                            className="modern-table"
                            pagination={paginationFactory({
                                sizePerPage: 100,
                                hideSizePerPage: true,
                                hidePageListOnlyOnePage: true
                            })}
                        />
                    </TableWrapper>
                )}
            </LoadingOverlay>

            {selectedObservation && (
                <ObservationDetailModal
                    data={selectedObservation}
                    onClose={() => setSelectedObservation(null)}
                />
            )}

            <GenericExportCSVModal
                show={showExportModal}
                onHide={() => setShowExportModal(false)}
                data={filteredObservations}
                columns={EXPORT_COLUMNS}
                title="Export Observations to CSV"
                filenamePrefix="observations_export"
            />
        </MainContainer>
    );
};

export default NectarObservations;
